import { getEnvTenantId, normalizeTenantId } from "@/lib/tenantId";
import { getAccessToken, getTenantId } from "@/lib/storeManager";
import ServiceDeskAPIService from "@sd/services/APIs";

const ENV_TENANT_ID = getEnvTenantId();

const RAW_BASE =
  typeof import.meta !== "undefined" &&
  import.meta.env &&
  typeof import.meta.env.VITE_API_BASE_URL === "string"
    ? import.meta.env.VITE_API_BASE_URL
    : "";

const API_ORIGIN_BASE = RAW_BASE.replace(/\/$/, "");

function apiBasePathname(): string {
  if (!API_ORIGIN_BASE) {
    return "";
  }
  try {
    return new URL(API_ORIGIN_BASE.endsWith("/") ? API_ORIGIN_BASE : `${API_ORIGIN_BASE}/`)
      .pathname.replace(/\/$/, "");
  } catch {
    return "";
  }
}

/** Host only (`https://ptsapi…`) — `/media` is not under `/api`. */
function apiHostOrigin(): string {
  if (!API_ORIGIN_BASE) {
    return "";
  }
  try {
    return new URL(
      API_ORIGIN_BASE.endsWith("/") ? API_ORIGIN_BASE : `${API_ORIGIN_BASE}/`
    ).origin;
  } catch {
    return "";
  }
}

/** HTTPS pages block `http://ptsapi…/media` (mixed content). Images already upgrade this. */
function forceHttpsHref(raw: string): string {
  const t = raw.trim();
  if (t.toLowerCase().startsWith("http://")) {
    return `https://${t.slice(7)}`;
  }
  return t;
}

function isStaticMediaPath(raw: string): boolean {
  const t = raw.trim();
  if (!t) {
    return false;
  }
  try {
    const path = /^https?:\/\//i.test(t)
      ? new URL(t).pathname
      : t.startsWith("/")
        ? t
        : `/${t}`;
    return path.startsWith("/media/");
  } catch {
    return /\/media\//i.test(t);
  }
}

/** Avoid `/api` + `/api/file-utility/...` when `file_path` is already an absolute API URL. */
function stripRedundantApiPrefix(pathname: string): string {
  const basePath = apiBasePathname();
  if (!basePath || basePath === "/") {
    return pathname;
  }
  if (pathname === basePath) {
    return "/";
  }
  if (pathname.startsWith(`${basePath}/`)) {
    return pathname.slice(basePath.length) || "/";
  }
  return pathname;
}

/**
 * Turns API-relative paths (eg `/media/…`, `/file-utility/…`) into an absolute URL
 * so `<img src>` hits the backend host instead of the Vite dev origin.
 */
export function resolveAbsoluteMediaHref(raw: string): string {
  const t = raw.trim();
  if (!t) {
    return "";
  }
  if (/^https?:\/\//i.test(t)) {
    return forceHttpsHref(t);
  }
  const path = t.startsWith("/") ? t : `/${t}`;
  if (path.startsWith("/media/")) {
    const origin = forceHttpsHref(apiHostOrigin());
    return origin ? `${origin}${path}` : path;
  }
  return API_ORIGIN_BASE ? forceHttpsHref(`${API_ORIGIN_BASE}${path}`) : path;
}

const FILE_UUID_RE = /^[0-9a-f-]{36}$/i;

/** Session cache — skip repeat download attempts for missing files (fewer 404s in Network). */
const failedFileIds = new Set<string>();
const failedMediaPaths = new Set<string>();

function fileIdFromMediaPath(path: string): string | null {
  const m = path.trim().match(/\/file-utility\/files\/([0-9a-f-]{36})(?:\/|$)/i);
  return m ? m[1]!.toLowerCase() : null;
}

const CANONICAL_DOWNLOAD_RE =
  /\/file-utility\/files\/[0-9a-f-]{36}\/download\/?$/i;

function isMediaPathBlocked(path: string): boolean {
  const t = path.trim();
  if (!t) {
    return true;
  }
  if (failedMediaPaths.has(t)) {
    return true;
  }
  const fid = fileIdFromMediaPath(t);
  // A 404 on /download/ must not skip file_path / media URLs for the same id.
  if (fid && failedFileIds.has(fid) && CANONICAL_DOWNLOAD_RE.test(t)) {
    return true;
  }
  return false;
}

function blockMediaPath(path: string, status?: number): void {
  if (status !== 404 && status !== 410) {
    return;
  }
  const t = path.trim();
  if (!t) {
    return;
  }
  failedMediaPaths.add(t);
  const fid = fileIdFromMediaPath(t);
  if (fid && CANONICAL_DOWNLOAD_RE.test(t)) {
    failedFileIds.add(fid);
  }
}

/** Try stored file_path first; /download/ is a fallback when metadata URLs fail. */
function preferDirectMediaPaths(candidates: string[]): string[] {
  const unique = [
    ...new Set(candidates.map(c => c.trim()).filter(Boolean)),
  ];
  const preferred = unique.filter(p => !CANONICAL_DOWNLOAD_RE.test(p));
  const downloads = unique.filter(p => CANONICAL_DOWNLOAD_RE.test(p));
  return [...preferred, ...downloads];
}

/** Skip download retries when this file id already returned 404. */
export function isAttachmentMediaBlocked(fileId: string): boolean {
  const id = fileId.trim().toLowerCase();
  return FILE_UUID_RE.test(id) && failedFileIds.has(id);
}

/** Clear session block after a failed preview — allows Retry to hit download again. */
export function clearAttachmentMediaBlock(fileId: string): void {
  const id = fileId.trim().toLowerCase();
  if (!FILE_UUID_RE.test(id)) {
    return;
  }
  failedFileIds.delete(id);
  for (const path of [...failedMediaPaths]) {
    if (fileIdFromMediaPath(path) === id) {
      failedMediaPaths.delete(path);
    }
  }
}

/** Public absolute URLs only — file-utility paths need authenticated blob fetch. */
export function canUseDirectMediaSrc(path: string): boolean {
  const t = path.trim();
  if (!t || isMediaPathBlocked(t)) {
    return false;
  }
  if (/\/file-utility\/files\//i.test(t)) {
    return false;
  }
  return /^https?:\/\//i.test(t);
}

function filterUnblockedPaths(paths: string[]): string[] {
  return paths.filter(p => !isMediaPathBlocked(p));
}

function httpStatusFromError(err: unknown): number | undefined {
  if (err && typeof err === "object" && "response" in err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    if (typeof status === "number") {
      return status;
    }
  }
  return undefined;
}

/** Expand bare file-utility UUIDs to download paths (common in form values). */
export function expandMediaPathCandidates(raw: string): string[] {
  const t = raw.trim();
  if (!t) {
    return [];
  }
  if (FILE_UUID_RE.test(t)) {
    if (isAttachmentMediaBlocked(t)) {
      return [];
    }
    return [`/file-utility/files/${t}/download/`];
  }
  return [t];
}

/** Path + query suitable for authenticated axios GET (same origin as API). */
export function mediaPathForAxios(raw: string): string | null {
  const t = raw.trim();
  if (!t) {
    return null;
  }
  if (FILE_UUID_RE.test(t)) {
    return `/file-utility/files/${t}/download/`;
  }
  if (isStaticMediaPath(t)) {
    // `/media/…` is not under VITE_API_BASE_URL (`…/api`). Axios would 404.
    return null;
  }
  if (/^https?:\/\//i.test(t)) {
    try {
      const base = API_ORIGIN_BASE || "";
      if (!base) {
        return null;
      }
      const u = new URL(t);
      const b = new URL(base.endsWith("/") ? base : `${base}/`);
      if (u.origin !== b.origin) {
        // Different host — axios accepts absolute URLs and ignores baseURL.
        return t;
      }
      const pathname = stripRedundantApiPrefix(`${u.pathname}${u.search}`);
      return pathname.startsWith("/") ? pathname : `/${pathname}`;
    } catch {
      return null;
    }
  }
  return t.startsWith("/") ? t : `/${t}`;
}

/** API responses are sometimes wrapped in `{ data: { … } }`. */
export function unwrapFileRecord(
  file: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!file || typeof file !== "object") {
    return null;
  }
  const inner = file.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return file;
}

export function fileDownloadPathsForId(id: string): string[] {
  const trimmed = id.trim();
  if (!FILE_UUID_RE.test(trimmed) || isAttachmentMediaBlocked(trimmed)) {
    return [];
  }
  return [
    `/file-utility/files/${trimmed}/download/`,
    `/file-utility/files/${trimmed}/download`,
  ];
}

export function collectAttachmentMediaPaths(
  file: Record<string, unknown> | null | undefined
): string[] {
  const record = unwrapFileRecord(file);
  if (!record) {
    return [];
  }
  const seen = new Set<string>();
  const paths: string[] = [];
  const add = (value: unknown) => {
    for (const candidate of expandMediaPathCandidates(String(value ?? ""))) {
      if (!candidate || seen.has(candidate)) {
        continue;
      }
      seen.add(candidate);
      paths.push(candidate);
    }
  };

  const id = String(record.id ?? record.pk ?? "").trim();
  if (FILE_UUID_RE.test(id)) {
    for (const p of fileDownloadPathsForId(id)) {
      add(p);
    }
  }

  add(record.file_path);
  add(record.url);
  add(record.file_url);
  add(record.path);
  add(record.file_path_thumbnail);
  add(record.thumbnail_url);
  add(record.thumbnail);

  if (FILE_UUID_RE.test(id)) {
    const download = `/file-utility/files/${id}/download/`;
    const downloadVariants = new Set(
      fileDownloadPathsForId(id).filter(p => p.includes("/download"))
    );
    const preferred = paths.filter(
      p => p !== download && !downloadVariants.has(p)
    );
    const fallbacks = paths.filter(
      p => p === download || downloadVariants.has(p)
    );
    if (preferred.length > 0) {
      return [...preferred, ...fallbacks.filter(p => !preferred.includes(p))];
    }
    const rest = paths.filter(p => p !== download);
    return [download, ...rest];
  }

  return paths;
}

function isLikelyImageBlobType(blob: Blob): boolean {
  const type = (blob.type ?? "").toLowerCase();
  if (type.startsWith("image/")) {
    return true;
  }
  if (
    type.includes("json") ||
    type.includes("html") ||
    type.includes("text") ||
    type.includes("xml")
  ) {
    return false;
  }
  return true;
}

async function blobHasImageMagicBytes(blob: Blob): Promise<boolean> {
  try {
    const buf = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    if (buf.length >= 2 && buf[0] === 0xff && buf[1] === 0xd8) {
      return true;
    }
    if (
      buf.length >= 8 &&
      buf[0] === 0x89 &&
      buf[1] === 0x50 &&
      buf[2] === 0x4e &&
      buf[3] === 0x47
    ) {
      return true;
    }
    if (
      buf.length >= 6 &&
      buf[0] === 0x47 &&
      buf[1] === 0x49 &&
      buf[2] === 0x46
    ) {
      return true;
    }
    if (buf.length >= 12 && buf[0] === 0x52 && buf[1] === 0x49 && buf[2] === 0x46 && buf[3] === 0x46) {
      const tag = String.fromCharCode(buf[8]!, buf[9]!, buf[10]!, buf[11]!);
      if (tag === "WEBP") {
        return true;
      }
    }
  } catch {
    return false;
  }
  return false;
}

async function blobLooksLikeNonImagePayload(blob: Blob): Promise<boolean> {
  try {
    const head = new Uint8Array(await blob.slice(0, 1).arrayBuffer());
    const c = head[0];
    return c === 0x7b || c === 0x3c; // { or <
  } catch {
    return true;
  }
}

async function isLikelyImageBlob(blob: Blob): Promise<boolean> {
  if (!blob || blob.size === 0) {
    return false;
  }
  if (!isLikelyImageBlobType(blob)) {
    return false;
  }
  const type = (blob.type ?? "").toLowerCase();
  if (type.startsWith("image/")) {
    return true;
  }
  if (await blobHasImageMagicBytes(blob)) {
    return true;
  }
  // Backends often omit content-type; reject obvious JSON/HTML error bodies.
  if (blob.size > 64 && !(await blobLooksLikeNonImagePayload(blob))) {
    return true;
  }
  return false;
}

async function blobHasVideoMagicBytes(blob: Blob): Promise<boolean> {
  try {
    const buf = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    if (
      buf.length >= 4 &&
      buf[0] === 0x1a &&
      buf[1] === 0x45 &&
      buf[2] === 0xdf &&
      buf[3] === 0xa3
    ) {
      return true;
    }
    if (buf.length >= 8) {
      const tag = String.fromCharCode(buf[4]!, buf[5]!, buf[6]!, buf[7]!);
      if (tag === "ftyp") {
        return true;
      }
    }
    if (buf.length >= 4) {
      const tag = String.fromCharCode(buf[0]!, buf[1]!, buf[2]!, buf[3]!);
      if (tag === "OggS") {
        return true;
      }
    }
  } catch {
    return false;
  }
  return false;
}

async function blobHasPdfMagicBytes(blob: Blob): Promise<boolean> {
  try {
    const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
    for (let i = 0; i <= Math.max(0, head.length - 4); i += 1) {
      if (
        head[i] === 0x25 &&
        head[i + 1] === 0x50 &&
        head[i + 2] === 0x44 &&
        head[i + 3] === 0x46
      ) {
        return true;
      }
    }
  } catch {
    return false;
  }
  return false;
}

async function isLikelyPdfBlob(
  blob: Blob,
  filenameHint?: string
): Promise<boolean> {
  if (!blob || blob.size === 0) {
    return false;
  }
  if (await blobHasPdfMagicBytes(blob)) {
    return true;
  }
  const type = (blob.type ?? "").toLowerCase();
  if (type === "application/pdf" || type.includes("pdf")) {
    return true;
  }
  if (type.includes("json") || type.includes("html") || type.includes("xml")) {
    return false;
  }
  if (filenameHint && /\.pdf$/i.test(filenameHint) && blob.size > 64) {
    try {
      const first = new Uint8Array(await blob.slice(0, 1).arrayBuffer())[0];
      return first !== 0x7b && first !== 0x3c;
    } catch {
      return true;
    }
  }
  return false;
}

/** Browsers need an application/pdf MIME on blob URLs when the API returns octet-stream. */
function ensurePdfBlobMime(blob: Blob): Blob {
  const type = (blob.type ?? "").toLowerCase();
  if (type === "application/pdf") {
    return blob;
  }
  return new Blob([blob], { type: "application/pdf" });
}

async function isLikelyVideoBlob(
  blob: Blob,
  filenameHint?: string
): Promise<boolean> {
  if (!blob || blob.size === 0) {
    return false;
  }
  const type = (blob.type ?? "").toLowerCase();
  // Always reject obvious JSON/HTML error bodies
  if (type.includes("json") || type.includes("html") || type.includes("xml")) {
    return false;
  }
  // If the content-type is already video, trust it immediately
  if (type.startsWith("video/")) {
    return true;
  }
  // If filenameHint clearly says it's a video file, trust the extension.
  // Only reject if the blob is clearly a JSON/HTML error body (first byte { or <).
  // On any read error, still trust the filename — don't silently drop real video blobs.
  if (filenameHint && /\.(webm|mp4|ogg|mov|avi)$/i.test(filenameHint) && blob.size > 256) {
    try {
      const head = new Uint8Array(await blob.slice(0, 1).arrayBuffer());
      const c = head[0];
      return c !== 0x7b && c !== 0x3c; // reject only if first byte is { (JSON) or < (HTML)
    } catch {
      return true; // can't read bytes but we have a video extension + size — trust it
    }
  }
  // No filename hint — fall back to magic bytes + heuristics
  if (await blobLooksLikeNonImagePayload(blob)) {
    return false;
  }
  if (await blobHasVideoMagicBytes(blob)) {
    return true;
  }
  if ((type === "" || type === "application/octet-stream") && blob.size > 256) {
    return true;
  }
  return false;
}

function axiosBlobGet(path: string, signal?: AbortSignal) {
  return ServiceDeskAPIService.files.getBlob(path, signal);
}

/** One in-flight fetch per file id — avoids duplicate parallel 404s. */
const inFlightBlobByFileId = new Map<string, Promise<Blob | null>>();

/** Download bytes with JWT (axios + fetch fallback for absolute media URLs). */
async function fetchMediaBlobBytes(
  filePathOrUrl: string,
  signal?: AbortSignal
): Promise<Blob | null> {
  const trimmed = filePathOrUrl.trim();
  const directId = FILE_UUID_RE.test(trimmed) ? trimmed.toLowerCase() : null;
  const pathId = fileIdFromMediaPath(trimmed);
  const fileId = directId ?? pathId;

  const isCanonicalDownload = /\/file-utility\/files\/[0-9a-f-]{36}\/download\/?$/i.test(
    trimmed
  );
  if (fileId && isAttachmentMediaBlocked(fileId) && isCanonicalDownload) {
    return null;
  }

  if (fileId && isCanonicalDownload && inFlightBlobByFileId.has(fileId)) {
    return inFlightBlobByFileId.get(fileId)!;
  }

  const run = async (): Promise<Blob | null> => {
    const candidates = expandMediaPathCandidates(filePathOrUrl);
    const paths =
      candidates.length > 0 ? candidates : [trimmed].filter(Boolean);

    for (const rawCandidate of filterUnblockedPaths(paths)) {
      const candidate = forceHttpsHref(rawCandidate);
      // Django `/file-utility/files/{id}/download/` 404s for these uploads.
      // The bytes live on `/media/…` (CORS allows GET; auth headers trigger a
      // preflight that static media does not answer).
      if (isStaticMediaPath(candidate)) {
        const absolute = /^https?:\/\//i.test(candidate)
          ? candidate
          : resolveAbsoluteMediaHref(candidate);
        try {
          const res = await fetch(absolute, { signal, mode: "cors" });
          if (res.ok) {
            const blob = await res.blob();
            if (blob.size > 0) {
              return blob;
            }
          } else {
            blockMediaPath(candidate, res.status);
          }
        } catch {
          /* HTTPS page + CORS: retry axios on the same absolute URL (no /api prefix). */
          try {
            const res = await axiosBlobGet(absolute, signal);
            const data = res?.data;
            if (data instanceof Blob && data.size > 0) {
              return data;
            }
          } catch {
            /* try next */
          }
        }
        continue;
      }

      const isAbsoluteUrl = /^https?:\/\//i.test(candidate);
      const axiosPath = isAbsoluteUrl ? null : mediaPathForAxios(candidate);

      if (axiosPath) {
        try {
          const res = await axiosBlobGet(axiosPath, signal);
          const data = res?.data;
          if (data instanceof Blob && data.size > 0) {
            return data;
          }
          if (typeof data === "string" && data.length > 0) {
            return new Blob([data], { type: "application/octet-stream" });
          }
        } catch (err) {
          blockMediaPath(axiosPath, httpStatusFromError(err));
        }
        continue;
      }

      const absolute = isAbsoluteUrl ? candidate : resolveAbsoluteMediaHref(candidate);
      if (!/^https?:\/\//i.test(absolute) || isMediaPathBlocked(absolute)) {
        continue;
      }

      try {
        const token = getAccessToken();
        const tenantId = (ENV_TENANT_ID || normalizeTenantId(getTenantId()) || "").trim();
        const headers: Record<string, string> = { Accept: "*/*" };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        if (tenantId) {
          headers["X-Tenant-Id"] = tenantId;
        }
        const res = await fetch(absolute, {
          headers,
          signal,
          credentials: "include",
        });
        if (res.ok) {
          const blob = await res.blob();
          if (blob.size > 0) {
            return blob;
          }
        } else {
          blockMediaPath(absolute, res.status);
        }
      } catch {
        /* try next */
      }
    }

    return null;
  };

  if (!fileId || !isCanonicalDownload) {
    return run();
  }

  const pending = run().finally(() => {
    inFlightBlobByFileId.delete(fileId);
  });
  inFlightBlobByFileId.set(fileId, pending);
  return pending;
}

export type MediaBlobKind = "image" | "video" | "pdf";

function mimeFromFilename(name: string): string | null {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "webm") {
    return "video/webm";
  }
  if (ext === "mp4") {
    return "video/mp4";
  }
  if (ext === "ogg") {
    return "video/ogg";
  }
  if (ext === "mov") {
    return "video/quicktime";
  }
  return null;
}

/** Browsers need a video/* MIME on blob URLs when the API returns octet-stream. */
function ensureVideoBlobMime(blob: Blob, filenameHint?: string): Blob {
  const type = (blob.type ?? "").toLowerCase();
  if (type.startsWith("video/")) {
    return blob;
  }
  const inferred = filenameHint ? mimeFromFilename(filenameHint) : null;
  if (inferred) {
    return new Blob([blob], { type: inferred });
  }
  if (type === "" || type === "application/octet-stream") {
    return new Blob([blob], { type: "video/webm" });
  }
  return blob;
}

/**
 * Fetch file bytes with the same auth headers as the rest of the app, then return a
 * temporary object URL (caller must revoke when done).
 */
export async function fetchMediaBlobObjectUrl(
  filePathOrUrl: string,
  kind: MediaBlobKind,
  signal?: AbortSignal,
  filenameHint?: string
): Promise<string | null> {
  const data = await fetchMediaBlobBytes(filePathOrUrl, signal);
  if (!data) {
    return null;
  }
  const valid =
    kind === "image"
      ? await isLikelyImageBlob(data)
      : kind === "pdf"
        ? await isLikelyPdfBlob(data, filenameHint)
        : await isLikelyVideoBlob(data, filenameHint);
  if (!valid) {
    return null;
  }
  const blob =
    kind === "video"
      ? ensureVideoBlobMime(data, filenameHint)
      : kind === "pdf"
        ? ensurePdfBlobMime(data)
        : data;
  return URL.createObjectURL(blob);
}

export async function fetchBlobObjectUrl(
  filePathOrUrl: string,
  signal?: AbortSignal
): Promise<string | null> {
  return fetchMediaBlobObjectUrl(filePathOrUrl, "image", signal);
}

/** Try several API paths until one returns media bytes (edit-form attachments vary by upload source). */
export async function fetchMediaBlobObjectUrlFromCandidates(
  candidates: string[],
  kind: MediaBlobKind,
  signal?: AbortSignal,
  filenameHint?: string
): Promise<string | null> {
  const unique = filterUnblockedPaths([
    ...new Set(candidates.map(c => c.trim()).filter(Boolean)),
  ]);
  if (!unique.length) {
    return null;
  }

  for (const candidate of unique) {
    if (isMediaPathBlocked(candidate)) {
      continue;
    }
    const url = await fetchMediaBlobObjectUrl(
      candidate,
      kind,
      signal,
      filenameHint
    );
    if (url) {
      return url;
    }
  }
  return null;
}

export async function fetchBlobObjectUrlFromCandidates(
  candidates: string[],
  signal?: AbortSignal
): Promise<string | null> {
  return fetchMediaBlobObjectUrlFromCandidates(candidates, "image", signal);
}

const blobUrlCache = new Map<string, string>();

export function invalidateBlobCacheEntry(cacheKey: string): void {
  const cached = blobUrlCache.get(cacheKey);
  if (cached) {
    URL.revokeObjectURL(cached);
    blobUrlCache.delete(cacheKey);
  }
}

/** Reuse blob URLs when reopening the same ticket attachment (avoids re-download). */
export async function getCachedMediaBlobObjectUrl(
  candidates: string[],
  cacheKey: string,
  kind: MediaBlobKind,
  signal?: AbortSignal,
  filenameHint?: string
): Promise<string | null> {
  const key = `${kind}:${cacheKey}`;
  const cached = blobUrlCache.get(key);
  if (cached) {
    return cached;
  }
  const blobUrl = await fetchMediaBlobObjectUrlFromCandidates(
    candidates,
    kind,
    signal,
    filenameHint
  );
  if (blobUrl) {
    blobUrlCache.set(key, blobUrl);
  }
  return blobUrl;
}

export async function getCachedBlobObjectUrl(
  candidates: string[],
  cacheKey: string,
  signal?: AbortSignal
): Promise<string | null> {
  return getCachedMediaBlobObjectUrl(candidates, cacheKey, "image", signal);
}

export async function getCachedPdfBlobObjectUrl(
  candidates: string[],
  cacheKey: string,
  signal?: AbortSignal,
  filenameHint?: string
): Promise<string | null> {
  const ordered = preferDirectMediaPaths(candidates);
  const sniffed = await getCachedMediaBlobObjectUrl(
    ordered,
    cacheKey,
    "pdf",
    signal,
    filenameHint
  );
  if (sniffed) {
    return sniffed;
  }
  const unique = filterUnblockedPaths(ordered);
  for (const candidate of unique) {
    const blob = await fetchMediaBlobBytes(candidate, signal);
    if (!blob || blob.size === 0) {
      continue;
    }
    if (await blobHasPdfMagicBytes(blob)) {
      const url = URL.createObjectURL(ensurePdfBlobMime(blob));
      blobUrlCache.set(`pdf:${cacheKey}`, url);
      return url;
    }
    if (isLikelyErrorDocumentBlob(blob) || (await blobLooksLikeNonImagePayload(blob))) {
      continue;
    }
    if (filenameHint && /\.pdf$/i.test(filenameHint) && blob.size > 64) {
      const url = URL.createObjectURL(ensurePdfBlobMime(blob));
      blobUrlCache.set(`pdf:${cacheKey}`, url);
      return url;
    }
  }
  return null;
}

export type PdfPreviewKind = "pdf" | "image";

function isLikelyErrorDocumentBlob(blob: Blob): boolean {
  const type = (blob.type ?? "").toLowerCase();
  return type.includes("json") || type.includes("html") || type.includes("xml");
}

function looksLikeImageAttachmentBlob(blob: Blob): Promise<boolean> {
  const type = (blob.type ?? "").toLowerCase();
  if (type.startsWith("image/")) {
    return Promise.resolve(true);
  }
  return blobHasImageMagicBytes(blob);
}

/**
 * Load a PDF (or screenshot saved with a .pdf name) using file metadata paths first.
 */
export async function getCachedPdfPreview(
  candidates: string[],
  cacheKey: string,
  filenameHint?: string,
  fileId?: string
): Promise<{ url: string; kind: PdfPreviewKind } | null> {
  const extra: string[] = [];
  const id = fileId?.trim() ?? "";
  if (id && FILE_UUID_RE.test(id)) {
    try {
      const res = await ServiceDeskAPIService.files.getById({ id });
      const record = unwrapFileRecord(
        res && typeof res === "object" ? (res as Record<string, unknown>) : null
      );
      if (record) {
        extra.push(...collectAttachmentMediaPaths({ ...record, id }));
      }
    } catch {
      /* use callers' candidates */
    }
  }

  const merged = preferDirectMediaPaths(
    [...extra, ...candidates].filter(
      p => p && !/thumbnail|thumb|_tn\b|\/tn\//i.test(p)
    )
  );

  for (const candidate of filterUnblockedPaths(merged)) {
    const blob = await fetchMediaBlobBytes(candidate);
    if (!blob || blob.size === 0) {
      continue;
    }
    if (await blobHasPdfMagicBytes(blob)) {
      const url = URL.createObjectURL(ensurePdfBlobMime(blob));
      blobUrlCache.set(`pdf:${cacheKey}`, url);
      return { url, kind: "pdf" };
    }
    if (await looksLikeImageAttachmentBlob(blob)) {
      const url = URL.createObjectURL(blob);
      blobUrlCache.set(`pdf:${cacheKey}`, url);
      return { url, kind: "image" };
    }
    if (isLikelyErrorDocumentBlob(blob) || (await blobLooksLikeNonImagePayload(blob))) {
      continue;
    }
    if (filenameHint && /\.pdf$/i.test(filenameHint) && blob.size > 64) {
      const url = URL.createObjectURL(ensurePdfBlobMime(blob));
      blobUrlCache.set(`pdf:${cacheKey}`, url);
      return { url, kind: "pdf" };
    }
  }

  const pdfUrl = await getCachedPdfBlobObjectUrl(
    merged,
    cacheKey,
    undefined,
    filenameHint
  );
  if (pdfUrl) {
    return { url: pdfUrl, kind: "pdf" };
  }

  return null;
}

export function videoFileCacheKey(fileId: string): string {
  return `video:file:${fileId.trim()}`;
}

/** Warm cache as soon as file metadata is known (edit form opens). */
export function prefetchVideoBlob(
  fileId: string,
  filenameHint?: string,
  extraCandidates: string[] = []
): void {
  const id = fileId.trim();
  if (!FILE_UUID_RE.test(id)) {
    return;
  }
  void getCachedVideoBlobObjectUrl(
    extraCandidates,
    `preload:${id}`,
    undefined,
    filenameHint,
    id
  );
}

export async function getCachedVideoBlobObjectUrl(
  candidates: string[],
  cacheKey: string,
  signal?: AbortSignal,
  filenameHint?: string,
  fileId?: string
): Promise<string | null> {
  const fid = fileId?.trim();
  const sessionKey = `video:${cacheKey}`;

  if (fid && FILE_UUID_RE.test(fid)) {
    if (isAttachmentMediaBlocked(fid)) {
      return getCachedMediaBlobObjectUrl(
        filterUnblockedPaths(candidates),
        cacheKey,
        "video",
        signal,
        filenameHint
      );
    }
    const fileKey = videoFileCacheKey(fid);
    const cached = blobUrlCache.get(fileKey) ?? blobUrlCache.get(sessionKey);
    if (cached) {
      blobUrlCache.set(fileKey, cached);
      blobUrlCache.set(sessionKey, cached);
      return cached;
    }
  }

  const idPaths = fid ? fileDownloadPathsForId(fid) : [];
  const merged = [
    ...new Set([...idPaths, ...candidates.map(c => c.trim()).filter(Boolean)]),
  ];
  return getCachedMediaBlobObjectUrl(
    merged,
    cacheKey,
    "video",
    signal,
    filenameHint
  );
}

/** Prefer direct download for full-size preview; thumbnails first for cards. */
export function collectAttachmentMediaPathsFromRecord(
  attachment: {
    id: string;
    url: string;
    thumbnailUrl?: string;
    mediaUrl?: string;
  },
  preferThumbnail = false
): string[] {
  const paths = collectAttachmentMediaPaths({
    id: attachment.id,
    file_path: attachment.mediaUrl ?? attachment.url,
    url: attachment.url,
    file_path_thumbnail: attachment.thumbnailUrl,
    thumbnail_url: attachment.thumbnailUrl,
  });

  if (preferThumbnail && attachment.thumbnailUrl?.trim()) {
    const thumb = attachment.thumbnailUrl.trim();
    return [thumb, ...paths.filter(p => p !== thumb)];
  }

  if (!preferThumbnail) {
    const media = attachment.mediaUrl?.trim();
    if (media) {
      const rest = paths.filter(p => p !== media);
      return preferDirectMediaPaths([media, ...rest]);
    }
    const id = attachment.id.trim();
    if (/^[0-9a-f-]{36}$/i.test(id) && !isAttachmentMediaBlocked(id)) {
      const download = `/file-utility/files/${id}/download/`;
      const rest = paths.filter(p => p !== download);
      return preferDirectMediaPaths([...rest, download]);
    }
  }

  return preferDirectMediaPaths(paths);
}

/** Resolve file metadata then download — used from STREAM so opening the ticket is not required. */
export async function downloadAttachmentByFileId(fileId: string): Promise<boolean> {
  const id = fileId.trim();
  if (!FILE_UUID_RE.test(id)) {
    return false;
  }
  const candidates = [
    `/file-utility/files/${id}/download/`,
    `/file-utility/files/${id}/download`,
  ];
  let filename = `attachment-${id.slice(0, 8)}`;
  try {
    const res = await ServiceDeskAPIService.files.getById({ id });
    const record = unwrapFileRecord(
      res && typeof res === "object" ? (res as Record<string, unknown>) : null
    );
    if (record) {
      const nameRaw =
        record.file_original_name ??
        record.original_name ??
        record.filename ??
        record.name ??
        "";
      const fromPath = String(record.file_path ?? "").split("/").pop() ?? "";
      filename = String(nameRaw || fromPath || filename);
      candidates.unshift(...collectAttachmentMediaPaths({ ...record, id }));
    }
  } catch {
    /* still try download paths */
  }
  return downloadAttachmentFile(preferDirectMediaPaths(candidates), filename);
}

/** Download any attachment (xlsx/doc/zip/etc.) with the same auth as preview. */
export async function downloadAttachmentFile(
  candidates: string[],
  filename: string
): Promise<boolean> {
  const unique = filterUnblockedPaths([
    ...new Set(candidates.map(c => c.trim()).filter(Boolean)),
  ]);
  for (const candidate of unique) {
    const blob = await fetchMediaBlobBytes(candidate);
    if (!blob || blob.size === 0) {
      continue;
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "attachment";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return true;
  }
  return false;
}
